import { useState, useEffect } from 'react';
import type { Order } from '@/services/orders';
import { Card } from './Card';

interface ProductionCardProps {
  readonly order: Order;
  readonly onSave: (data: Record<string, unknown>) => void;
  readonly isPending: boolean;
  readonly highlighted?: boolean;
}

interface ProductionForm {
  printerProfile: string;
  slicerProfile: string;
  bambuPreset: string;
  bedType: string;
  materialBrand: string;
  materialColorCode: string;
  dryingRequired: boolean;
  printSuccess: string;
  failureCategory: string;
  failureNotes: string;
  reprintCount: number;
  qcPass: string;
  qcNotes: string;
}

const initialForm = (prod: Order['production']): ProductionForm => ({
  printerProfile: prod?.printerProfile ?? '',
  slicerProfile: prod?.slicerProfile ?? '',
  bambuPreset: prod?.bambuPreset ?? '',
  bedType: prod?.bedType ?? 'Textured PEI',
  materialBrand: prod?.materialBrand ?? '',
  materialColorCode: prod?.materialColorCode ?? '',
  dryingRequired: prod?.dryingRequired ?? false,
  printSuccess: prod?.printSuccess === null ? 'Pending' : prod?.printSuccess ? 'Success' : 'Failed',
  failureCategory: prod?.failureCategory ?? 'N/A',
  failureNotes: prod?.failureNotes ?? '',
  reprintCount: prod?.reprintCount ?? 0,
  qcPass: prod?.qcPass === null ? 'Pending' : prod?.qcPass ? 'Pass' : 'Fail',
  qcNotes: prod?.qcNotes ?? '',
});

export const ProductionCard = ({ order, onSave, isPending, highlighted }: ProductionCardProps) => {
  const prod = order.production;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ProductionForm>(initialForm(prod));

  // Sync form when production data changes externally (and not editing)
  useEffect(() => {
    if (!editing) {
      setForm(initialForm(prod));
    }
  }, [prod, editing]);

  const updateField = <K extends keyof ProductionForm>(key: K, value: ProductionForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave({
      production: {
        printerProfile: form.printerProfile || null,
        slicerProfile: form.slicerProfile || null,
        bambuPreset: form.bambuPreset || null,
        bedType: form.bedType || null,
        materialBrand: form.materialBrand || null,
        materialColorCode: form.materialColorCode || null,
        dryingRequired: form.dryingRequired,
        printSuccess: form.printSuccess === 'Pending' ? null : form.printSuccess === 'Success',
        failureCategory: form.failureCategory === 'N/A' ? null : form.failureCategory,
        failureNotes: form.failureNotes || null,
        reprintCount: form.reprintCount,
        qcPass: form.qcPass === 'Pending' ? null : form.qcPass === 'Pass',
        qcNotes: form.qcNotes || null,
      },
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setForm(initialForm(prod));
    setEditing(false);
  };

  return (
    <Card highlighted={highlighted}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
          Production
        </div>
        {!editing && prod && (
          <span
            onClick={() => setEditing(true)}
            style={{ fontSize: '12px', color: 'var(--text-accent)', cursor: 'pointer' }}
          >
            Edit
          </span>
        )}
      </div>

      {/* Read mode - no data */}
      {!editing && !prod && (
        <div>
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
            No production data yet
          </div>
          <button
            onClick={() => setEditing(true)}
            style={{
              fontSize: '12px',
              marginTop: '8px',
              background: 'none',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              padding: '6px 12px',
              cursor: 'pointer',
              color: 'var(--text-primary)',
            }}
          >
            + Add production data
          </button>
        </div>
      )}

      {/* Read mode - has data */}
      {!editing && prod && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 16px', fontSize: '13px' }}>
          {prod.printerProfile && <ReadRow label="Printer" value={prod.printerProfile} />}
          {prod.slicerProfile && <ReadRow label="Slicer" value={prod.slicerProfile} />}
          {prod.bambuPreset && <ReadRow label="Preset" value={prod.bambuPreset} />}
          {prod.bedType && <ReadRow label="Bed type" value={prod.bedType} />}
          {prod.materialBrand && <ReadRow label="Brand" value={prod.materialBrand} />}
          {prod.materialColorCode && <ReadRow label="Color code" value={prod.materialColorCode} />}
          <ReadRow label="Drying" value={prod.dryingRequired ? 'Yes' : 'No'} />
          <ReadRow
            label="Result"
            value={prod.printSuccess === null ? 'Pending' : prod.printSuccess ? 'Success' : 'Failed'}
            statusColor={prod.printSuccess === true ? 'success' : prod.printSuccess === false ? 'error' : undefined}
          />
          {prod.failureCategory && <ReadRow label="Failure" value={prod.failureCategory} />}
          <ReadRow label="Reprints" value={String(prod.reprintCount)} />
          <ReadRow
            label="QC"
            value={prod.qcPass === null ? 'Pending' : prod.qcPass ? 'Pass' : 'Fail'}
            statusColor={prod.qcPass === true ? 'success' : prod.qcPass === false ? 'error' : undefined}
          />
          {prod.qcNotes && <ReadRow label="QC notes" value={prod.qcNotes} />}
        </div>
      )}

      {/* Edit mode */}
      {editing && (
        <div>
          {/* Setup group */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 16px', fontSize: '13px' }}>
            <FormRow label="Printer profile">
              <select className="admin-input" value={form.printerProfile} onChange={(e) => updateField('printerProfile', e.target.value)} style={{ fontSize: '12px' }}>
                <option value="">Select printer...</option>
                <option>Bambu X1C #1</option>
                <option>Bambu X1C #2</option>
                <option>Bambu P1S</option>
              </select>
            </FormRow>
            <FormRow label="Slicer profile">
              <select className="admin-input" value={form.slicerProfile} onChange={(e) => updateField('slicerProfile', e.target.value)} style={{ fontSize: '12px' }}>
                <option value="">Select profile...</option>
                <option>Standard 0.2mm</option>
                <option>Fine 0.12mm</option>
                <option>Draft 0.28mm</option>
              </select>
            </FormRow>
            <FormRow label="Bambu preset">
              <input className="admin-input" value={form.bambuPreset} onChange={(e) => updateField('bambuPreset', e.target.value)} placeholder="e.g. PA-CF 0.4" style={{ fontSize: '12px' }} />
            </FormRow>
            <FormRow label="Bed type">
              <select className="admin-input" value={form.bedType} onChange={(e) => updateField('bedType', e.target.value)} style={{ fontSize: '12px' }}>
                <option>Textured PEI</option>
                <option>Smooth PEI</option>
                <option>Cool plate</option>
              </select>
            </FormRow>
            <FormRow label="Material brand">
              <input className="admin-input" value={form.materialBrand} onChange={(e) => updateField('materialBrand', e.target.value)} placeholder="e.g. Polymaker" style={{ fontSize: '12px' }} />
            </FormRow>
            <FormRow label="Color code">
              <input className="admin-input" value={form.materialColorCode} onChange={(e) => updateField('materialColorCode', e.target.value)} placeholder="e.g. #1A1A1A" style={{ fontSize: '12px' }} />
            </FormRow>
            <FormRow label="Drying required">
              <label className="flex items-center" style={{ gap: '6px', fontSize: '12px', padding: '4px 0' }}>
                <input type="checkbox" checked={form.dryingRequired} onChange={(e) => updateField('dryingRequired', e.target.checked)} />
                Yes, material needs drying
              </label>
            </FormRow>
          </div>

          <Divider />

          {/* Result group */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 16px', fontSize: '13px' }}>
            <FormRow label="Print result">
              <select className="admin-input" value={form.printSuccess} onChange={(e) => updateField('printSuccess', e.target.value)} style={{ fontSize: '12px' }}>
                <option>Pending</option>
                <option>Success</option>
                <option>Failed</option>
              </select>
            </FormRow>
            <FormRow label="Failure category">
              <select
                className="admin-input"
                value={form.failureCategory}
                onChange={(e) => updateField('failureCategory', e.target.value)}
                disabled={form.printSuccess !== 'Failed'}
                style={{ fontSize: '12px', opacity: form.printSuccess !== 'Failed' ? 0.5 : 1 }}
              >
                <option>N/A</option>
                <option>Adhesion</option>
                <option>Stringing</option>
                <option>Layer shift</option>
                <option>Spaghetti</option>
                <option>Machine error</option>
                <option>Other</option>
              </select>
            </FormRow>
            <FormRow label="Failure notes">
              <input
                className="admin-input"
                value={form.failureNotes}
                onChange={(e) => updateField('failureNotes', e.target.value)}
                placeholder="Details..."
                disabled={form.printSuccess !== 'Failed'}
                style={{ fontSize: '12px', opacity: form.printSuccess !== 'Failed' ? 0.5 : 1 }}
              />
            </FormRow>
            <FormRow label="Reprint count">
              <input
                className="admin-input"
                type="number"
                min="0"
                value={form.reprintCount}
                onChange={(e) => updateField('reprintCount', parseInt(e.target.value, 10) || 0)}
                style={{ fontSize: '12px', width: '60px' }}
              />
            </FormRow>
          </div>

          <Divider />

          {/* QC group */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 16px', fontSize: '13px' }}>
            <FormRow label="QC result">
              <select className="admin-input" value={form.qcPass} onChange={(e) => updateField('qcPass', e.target.value)} style={{ fontSize: '12px' }}>
                <option>Pending</option>
                <option>Pass</option>
                <option>Fail</option>
              </select>
            </FormRow>
            <FormRow label="QC notes">
              <input className="admin-input" value={form.qcNotes} onChange={(e) => updateField('qcNotes', e.target.value)} placeholder="QC observations..." style={{ fontSize: '12px' }} />
            </FormRow>
          </div>

          {/* Footer */}
          <div className="flex justify-end" style={{ gap: '8px', marginTop: '12px' }}>
            <button
              onClick={handleCancel}
              style={{
                fontSize: '12px',
                padding: '6px 14px',
                minHeight: '36px',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isPending}
              style={{
                fontSize: '12px',
                padding: '6px 14px',
                minHeight: '36px',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--text-primary)',
                color: 'var(--bg-surface)',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontWeight: 'var(--font-weight-medium)',
                opacity: isPending ? 0.6 : 1,
              }}
            >
              {isPending ? 'Saving...' : 'Save production data'}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

// Sub-components

const ReadRow = ({
  label,
  value,
  statusColor,
}: {
  readonly label: string;
  readonly value: string;
  readonly statusColor?: 'success' | 'error';
}) => (
  <div style={{ padding: '4px 0' }}>
    <div style={{ color: 'var(--text-tertiary)', fontSize: '12px', marginBottom: '2px' }}>{label}</div>
    <div>
      {value}
      {statusColor && (
        <span
          style={{
            fontSize: '11px',
            fontWeight: 'var(--font-weight-medium)',
            padding: '1px 6px',
            borderRadius: '4px',
            marginLeft: '6px',
            backgroundColor: statusColor === 'success' ? 'var(--bg-success-subtle)' : 'var(--bg-error-subtle)',
            color: statusColor === 'success' ? 'var(--color-success-600)' : 'var(--color-error-600)',
          }}
        >
          {statusColor === 'success' ? 'OK' : '!'}
        </span>
      )}
    </div>
  </div>
);

const FormRow = ({ label, children }: { readonly label: string; readonly children: React.ReactNode }) => (
  <div style={{ padding: '4px 0' }}>
    <div style={{ color: 'var(--text-tertiary)', fontSize: '12px', marginBottom: '4px' }}>{label}</div>
    <div>{children}</div>
  </div>
);

const Divider = () => (
  <div style={{ height: '0.5px', backgroundColor: 'var(--border-default)', margin: '8px 0' }} />
);
